import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Search, 
  Copy, 
  Check, 
  ExternalLink, 
  QrCode, 
  Download,
  X,
  Grid3X3,
  List,
  ChevronDown,
  ChevronUp,
  Globe,
  BookOpen,
  Monitor,
  FileText,
  GraduationCap,
  Building2,
  Shield,
  Lock,
  Scale,
  Layers
} from 'lucide-react';
import { siteRoutes, ROUTE_GROUPS } from '../data/siteRoutes';

const BASE_URL = 'https://thirdcultureleadership.com';

// Icon mapping for groups
const groupIcons = {
  [ROUTE_GROUPS.CORE]: Globe,
  [ROUTE_GROUPS.BOOKS]: BookOpen,
  [ROUTE_GROUPS.BOOK_READER]: Monitor,
  [ROUTE_GROUPS.ARTICLES]: FileText,
  [ROUTE_GROUPS.COURSES]: GraduationCap,
  [ROUTE_GROUPS.PUBLISHING]: Building2,
  [ROUTE_GROUPS.ADMIN]: Shield,
  [ROUTE_GROUPS.AUTH]: Lock,
  [ROUTE_GROUPS.LEGAL]: Scale,
  [ROUTE_GROUPS.OTHER]: Layers
};

const SiteLinksDirectory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [copiedId, setCopiedId] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [allLinksCopied, setAllLinksCopied] = useState(false);

  // Filter only public routes for the public directory
  const publicRoutes = useMemo(() => {
    return siteRoutes.filter(route => route.isPublic);
  }, []);

  // Get unique groups from public routes
  const groups = useMemo(() => {
    const groupSet = new Set(publicRoutes.map(r => r.group));
    return Array.from(groupSet);
  }, [publicRoutes]);

  // Filter routes based on search and selected group
  const filteredRoutes = useMemo(() => {
    return publicRoutes.filter(route => {
      const matchesSearch = !searchQuery || 
        route.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        route.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
        route.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGroup = selectedGroup === 'all' || route.group === selectedGroup;
      
      return matchesSearch && matchesGroup;
    });
  }, [publicRoutes, searchQuery, selectedGroup]);

  // Group routes by category
  const groupedRoutes = useMemo(() => {
    const grouped = {};
    filteredRoutes.forEach(route => {
      if (!grouped[route.group]) {
        grouped[route.group] = [];
      }
      grouped[route.group].push(route);
    });
    return grouped;
  }, [filteredRoutes]);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAllLinks = async () => {
    const linksList = publicRoutes
      .map(route => `${route.name}: ${BASE_URL}${route.path}`)
      .join('\n');
    
    try {
      await navigator.clipboard.writeText(linksList);
      setAllLinksCopied(true);
      setTimeout(() => setAllLinksCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy all links:', err);
    }
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const getFullUrl = (path) => `${BASE_URL}${path}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-soft-grey to-white pt-8 pb-20 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-navy/10 rounded-2xl mb-4">
            <QrCode className="w-8 h-8 text-navy" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-navy mb-4">
            Site Directory
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-2">
            Quick access to all pages on Third Culture Leadership with scannable QR codes
          </p>
          <p className="text-sm text-slate-500">
            {publicRoutes.length} pages • {groups.length} categories
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/30 transition-all"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Group Filter */}
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-navy/20 cursor-pointer"
              >
                <option value="all">All Categories</option>
                {groups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>

              {/* View Mode Toggle */}
              <div className="flex bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-navy' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <Grid3X3 size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-navy' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <List size={20} />
                </button>
              </div>

              {/* Copy All Button */}
              <button
                onClick={copyAllLinks}
                className="flex items-center gap-2 px-4 py-3 bg-navy text-white rounded-xl hover:bg-navy/90 transition-colors"
              >
                {allLinksCopied ? <Check size={18} /> : <Copy size={18} />}
                <span className="hidden sm:inline">{allLinksCopied ? 'Copied!' : 'Copy All Links'}</span>
              </button>

              {/* Download QR Pack (placeholder) */}
              <button
                className="flex items-center gap-2 px-4 py-3 bg-gold text-navy rounded-xl hover:bg-gold/90 transition-colors"
                title="Coming soon"
              >
                <Download size={18} />
                <span className="hidden sm:inline">QR Pack</span>
              </button>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
            Showing {filteredRoutes.length} of {publicRoutes.length} pages
          </div>
        </div>

        {/* Routes Display */}
        {viewMode === 'grid' ? (
          // Grid View - Grouped
          <div className="space-y-8">
            {Object.entries(groupedRoutes).map(([group, routes]) => {
              const GroupIcon = groupIcons[group] || Layers;
              const isExpanded = expandedGroups[group] !== false; // Default to expanded
              
              return (
                <div key={group} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(group)}
                    className="w-full flex items-center justify-between p-6 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center">
                        <GroupIcon className="w-5 h-5 text-navy" />
                      </div>
                      <div className="text-left">
                        <h2 className="font-semibold text-navy text-lg">{group}</h2>
                        <p className="text-sm text-slate-500">{routes.length} pages</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="text-slate-400" /> : <ChevronDown className="text-slate-400" />}
                  </button>

                  {/* Group Content */}
                  {isExpanded && (
                    <div className="p-6 pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {routes.map(route => (
                          <div
                            key={route.id}
                            className="bg-slate-50 hover:bg-slate-100 rounded-xl p-4 transition-all group"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-navy truncate">{route.name}</h3>
                                <p className="text-xs text-slate-500 truncate">{route.path}</p>
                              </div>
                              <button
                                onClick={() => setQrModalOpen(route.id)}
                                className="p-2 bg-white rounded-lg shadow-sm hover:shadow transition-shadow"
                                title="View QR Code"
                              >
                                <QrCode size={16} className="text-navy" />
                              </button>
                            </div>
                            
                            <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                              {route.description}
                            </p>

                            <div className="flex items-center gap-2">
                              <Link
                                to={route.path}
                                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-navy text-white text-sm rounded-lg hover:bg-navy/90 transition-colors"
                              >
                                <ExternalLink size={14} />
                                Visit
                              </Link>
                              <button
                                onClick={() => copyToClipboard(getFullUrl(route.path), route.id)}
                                className="flex items-center justify-center gap-1 px-3 py-2 bg-white border border-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-50 transition-colors"
                              >
                                {copiedId === route.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                                {copiedId === route.id ? 'Copied' : 'Copy'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {filteredRoutes.map(route => {
                const GroupIcon = groupIcons[route.group] || Layers;
                return (
                  <div
                    key={route.id}
                    className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <GroupIcon className="w-5 h-5 text-slate-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-navy">{route.name}</h3>
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
                          {route.group}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">{route.path}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setQrModalOpen(route.id)}
                        className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                        title="View QR Code"
                      >
                        <QrCode size={18} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(getFullUrl(route.path), route.id)}
                        className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                        title="Copy Link"
                      >
                        {copiedId === route.id ? (
                          <Check size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} className="text-slate-600" />
                        )}
                      </button>
                      <Link
                        to={route.path}
                        className="p-2 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors"
                        title="Visit Page"
                      >
                        <ExternalLink size={18} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredRoutes.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">No pages found</h3>
            <p className="text-slate-500">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* QR Modal */}
        {qrModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setQrModalOpen(null)}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <button
                onClick={() => setQrModalOpen(null)}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>

              {(() => {
                const route = siteRoutes.find(r => r.id === qrModalOpen);
                if (!route) return null;
                
                return (
                  <>
                    <div className="text-center mb-6">
                      <h3 className="font-semibold text-navy text-lg mb-1">{route.name}</h3>
                      <p className="text-sm text-slate-500">{route.path}</p>
                    </div>

                    <div className="flex justify-center mb-6">
                      <div className="p-4 bg-white border-2 border-slate-200 rounded-2xl">
                        <QRCodeSVG
                          value={getFullUrl(route.path)}
                          size={200}
                          level="H"
                          includeMargin={false}
                          bgColor="#ffffff"
                          fgColor="#1a365d"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 text-center mb-3">
                        Scan to visit this page
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(getFullUrl(route.path), `modal-${route.id}`)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
                        >
                          {copiedId === `modal-${route.id}` ? <Check size={18} /> : <Copy size={18} />}
                          {copiedId === `modal-${route.id}` ? 'Copied!' : 'Copy Link'}
                        </button>
                        <Link
                          to={route.path}
                          onClick={() => setQrModalOpen(null)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-navy text-white rounded-xl hover:bg-navy/90 transition-colors"
                        >
                          <ExternalLink size={18} />
                          Visit
                        </Link>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500 mb-4">
            Need admin access to manage links?
          </p>
          <Link
            to="/admin/links"
            className="inline-flex items-center gap-2 text-navy hover:text-gold transition-colors font-medium"
          >
            <Shield size={16} />
            Admin Links Manager
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SiteLinksDirectory;
